/** Representative block speed: cruise, climb and descent averaged over a sector. */
export const LEG_CRUISE_KMH = 750;
/** Gate-to-gate overhead the sector distance cannot express (taxi, approach, holding). */
export const LEG_GROUND_OVERHEAD_MINUTES = 30;
/** A leg with no distance on record cannot have a derived block, so it gets a short one. */
export const LEG_FALLBACK_BLOCK_MINUTES = 120;
export function legBlockMinutes(distanceKm) {
    if (distanceKm === null || !Number.isFinite(distanceKm))
        return LEG_FALLBACK_BLOCK_MINUTES;
    return Math.round((distanceKm / LEG_CRUISE_KMH) * 60) + LEG_GROUND_OVERHEAD_MINUTES;
}
/**
 * Both ends of the leg, in the airport's own clock.
 *
 * A departure's published time is when it left, so that end is the anchor and the landing is
 * derived. An arrival's published time is when it lands, so the roles swap.
 */
export function legTiming(input) {
    const published = input.actualAt ?? input.estimatedAt ?? input.scheduledAt;
    const anchor = published instanceof Date ? published : new Date(published);
    const blockMs = legBlockMinutes(input.distanceKm) * 60_000;
    const basis = input.actualAt === null ? "time" : "board";
    if (input.direction === "departure") {
        return { takeoffAt: anchor, landingAt: new Date(anchor.getTime() + blockMs), basis };
    }
    return { takeoffAt: new Date(anchor.getTime() - blockMs), landingAt: anchor, basis };
}
/**
 * The phase at a moment.
 *
 * A cancelled board row is cancelled whatever the clock says, and a row the board has already
 * landed is landed even if our derived block would still have it in the air. Below that the
 * clock decides, with the board's own "departed" breaking a tie in its favour — an early
 * departure the board published is a fact, and the schedule it contradicts is not.
 */
export function legPhaseAt(timing, status, now) {
    const words = (status ?? "").trim().toUpperCase();
    if (words.includes("CANCEL"))
        return "cancelled";
    if (words.includes("LANDED") || words.includes("ARRIVED"))
        return "landed";
    if (now.getTime() >= timing.landingAt.getTime())
        return "landed";
    if (words.includes("DEPARTED") || words.includes("EN ROUTE") || words.includes("AIRBORNE")) {
        return "in_progress";
    }
    if (now.getTime() >= timing.takeoffAt.getTime())
        return "in_progress";
    return "scheduled";
}
/** How far along the leg a flight is, 0 at takeoff and 1 at landing. */
export function legProgressAt(timing, now) {
    const total = timing.landingAt.getTime() - timing.takeoffAt.getTime();
    if (total <= 0)
        return 1;
    const elapsed = now.getTime() - timing.takeoffAt.getTime();
    return Math.min(1, Math.max(0, elapsed / total));
}
/** The seatbelt sign is lit while the aircraft is climbing out of its origin. */
export const SEATBELT_TAKEOFF_MS = 15 * 60_000;
/** And comes back on for the descent and landing. */
export const SEATBELT_LANDING_MS = 15 * 60_000;
/**
 * Whether the seatbelt sign is on: the first 15 minutes of the leg and the last 15.
 *
 * The two windows together are a short hop's whole flight time, which is what its sign actually does
 * - a 30-minute flight keeps it on from gate to gate - while a longer sector has the cruise in
 * between, and the sign coming back on is the descent rather than a decoration.
 */
export function seatbeltOn(timing, now) {
    const at = now.getTime();
    return (at - timing.takeoffAt.getTime() < SEATBELT_TAKEOFF_MS ||
        timing.landingAt.getTime() - at < SEATBELT_LANDING_MS);
}
//# sourceMappingURL=flight-timing.js.map